/*
  CODEX CUSTOMIZATION START
  File: custom-product.js
  Scope: product templates
  Notes:
  - Keeps product-page JavaScript separate from Impact core files.
  - Adds a compare-at price to the custom price card when Google Automated Discounts lowers the visible product price client-side.
  CODEX CUSTOMIZATION END
*/

(() => {
  const PRODUCT_INFO_SELECTOR = '.shopify-section--main-product .product-info';
  const CUSTOM_PRICE_BLOCK_SELECTOR = '.product-info__block-item[data-block-type="custom-price"]';
  const GOOGLE_DISCOUNT_BLOCK_SELECTOR = '.product-info__block-item[data-block-id*="google_youtube_automated_discounts_price"]';
  const PRICE_MAIN_COMPARE_CLASS = 'product-info__custom-price-main--has-compare-at';
  const CODEX_ACTIVE_ATTRIBUTE = 'data-codex-google-automated-discount-active';
  const CODEX_COMPARE_ATTRIBUTE = 'data-codex-google-automated-discount-compare';
  const CODEX_HIDDEN_COMPARE_ATTRIBUTE = 'data-codex-google-automated-discount-hidden-compare';
  const CODEX_BADGE_ATTRIBUTE = 'data-codex-google-automated-discount-badge';
  const CODEX_BADGE_LIST_ATTRIBUTE = 'data-codex-google-automated-discount-badge-list';
  const CODEX_PRICE_MAIN_COMPARE_ATTRIBUTE = 'data-codex-google-automated-discount-main-class';
  const CODEX_LIQUID_COMPARE_ATTRIBUTE = 'data-codex-google-automated-discount-liquid-compare';
  const CODEX_ORIGINAL_PRICE_CENTS_ATTRIBUTE = 'data-codex-google-automated-discount-original-price-cents';
  const CODEX_ORIGINAL_PRICE_TEXT_ATTRIBUTE = 'data-codex-google-automated-discount-original-price-text';
  const CODEX_HIDDEN_PRICE_LIST_ITEM_ATTRIBUTE = 'data-codex-google-automated-discount-hidden-price-list-item';
  const CODEX_COMPARE_TEXT_ATTRIBUTE = 'data-codex-google-automated-discount-compare-text';
  const CODEX_COMPARE_SELECTOR = `[${CODEX_COMPARE_ATTRIBUTE}]`;
  const CODEX_BADGE_SELECTOR = `on-sale-badge[${CODEX_BADGE_ATTRIBUTE}]`;
  const SYNC_DELAYS = [0, 250, 750, 1500, 3000, 5000, 8000];
  const COMPARE_ENFORCEMENT_DELAYS = [0, 50, 150, 300, 750, 1500, 3000, 5000];

  let syncQueued = false;
  let syncInProgress = false;
  let syncAfterCurrentRun = false;
  let observer = null;
  let observedProductInfo = null;
  let productJsonPath = '';
  let productJsonPromise = null;
  let compareEnforcementScheduled = false;

  function parseMoney(value) {
    if (typeof value !== 'string') {
      return NaN;
    }

    const match = value.replace(/\s+/g, ' ').match(/-?\d[\d,]*(?:\.\d+)?/);

    if (!match) {
      return NaN;
    }

    return Number.parseFloat(match[0].replace(/,/g, ''));
  }

  function parseCartAttributes(scriptText) {
    const cartAttributesMatch = scriptText.match(/automatedDiscountsLiquidData\.cartAttributes\s*=\s*({[^\n]*})/);

    if (!cartAttributesMatch) {
      return {};
    }

    try {
      return JSON.parse(cartAttributesMatch[1]);
    } catch (error) {
      return {};
    }
  }

  function getInlineGoogleDiscountData(appBlock) {
    const inlineScript = appBlock ? appBlock.querySelector('script:not([src])') : null;
    const scriptText = inlineScript ? inlineScript.textContent || '' : '';
    const currentPriceMatch = scriptText.match(/automatedDiscountsLiquidData\.currentPrice\s*=\s*([0-9.]+)(?:\s*\/\s*([0-9.]+))?/);
    const formattedCurrentPriceMatch = scriptText.match(/automatedDiscountsLiquidData\.formattedCurrentPrice\s*=\s*(["'])(.*?)\1/);
    const variantIdMatch = scriptText.match(/automatedDiscountsLiquidData\.variantId\s*=\s*(["'])(.*?)\1/);
    let currentPrice = NaN;

    if (currentPriceMatch) {
      currentPrice = Number.parseFloat(currentPriceMatch[1]);

      if (currentPriceMatch[2]) {
        currentPrice /= Number.parseFloat(currentPriceMatch[2]);
      }
    }

    return {
      currentPrice,
      formattedCurrentPrice: formattedCurrentPriceMatch ? formattedCurrentPriceMatch[2] : '',
      variantId: variantIdMatch ? variantIdMatch[2] : '',
      cartAttributes: parseCartAttributes(scriptText),
    };
  }

  function getGoogleDiscountData(appBlock) {
    const inlineData = getInlineGoogleDiscountData(appBlock);

    if (inlineData.variantId || inlineData.formattedCurrentPrice || Number.isFinite(inlineData.currentPrice) || Object.keys(inlineData.cartAttributes).length > 0) {
      return inlineData;
    }

    const globalData = window.automatedDiscountsLiquidData;

    if (globalData && typeof globalData === 'object') {
      return {
        currentPrice: Number(globalData.currentPrice),
        formattedCurrentPrice: globalData.formattedCurrentPrice || '',
        variantId: String(globalData.variantId || ''),
        cartAttributes: globalData.cartAttributes || {},
      };
    }

    return inlineData;
  }

  function decodeGoogleOffer(cartAttributes, variantId) {
    const encodedOffer = cartAttributes && typeof cartAttributes._gad === 'string' ? cartAttributes._gad : '';

    if (encodedOffer === '' || typeof window.atob !== 'function') {
      return {
        price: NaN,
      };
    }

    try {
      const decodedOffer = JSON.parse(window.atob(encodedOffer));
      const offerRecord = decodedOffer && decodedOffer.R ? decodedOffer.R : null;
      const offerId = offerRecord ? String(offerRecord.offerId || '') : '';
      const offerVariantId = offerId.split('_').pop();

      if (variantId && offerVariantId && offerVariantId !== String(variantId)) {
        return {
          price: NaN,
        };
      }

      return {
        price: Number(offerRecord ? offerRecord.price : NaN),
      };
    } catch (error) {
      return {
        price: NaN,
      };
    }
  }

  function decodeBase64Url(value) {
    const normalizedValue = value.replace(/-/g, '+').replace(/_/g, '/');
    const padding = '='.repeat((4 - (normalizedValue.length % 4)) % 4);

    return window.atob(`${normalizedValue}${padding}`);
  }

  function getPv2Token() {
    const currentParams = new URLSearchParams(window.location.search);

    if (currentParams.has('pv2')) {
      return currentParams.get('pv2') || '';
    }

    const shopifyPageUrl = window.__st && typeof window.__st.pageurl === 'string' ? window.__st.pageurl : '';
    const queryStart = shopifyPageUrl.indexOf('?');

    if (queryStart === -1) {
      return '';
    }

    return new URLSearchParams(shopifyPageUrl.slice(queryStart)).get('pv2') || '';
  }

  function hasGoogleOfferSignal() {
    if (getPv2Token() !== '') {
      return true;
    }

    const productInfo = document.querySelector(PRODUCT_INFO_SELECTOR);
    const appBlock = productInfo ? productInfo.querySelector(GOOGLE_DISCOUNT_BLOCK_SELECTOR) : null;

    if (!appBlock) {
      return false;
    }

    const inlineScript = appBlock.querySelector('script:not([src])');
    const scriptText = inlineScript ? inlineScript.textContent || '' : '';
    const cartAttributes = parseCartAttributes(scriptText);

    if (typeof cartAttributes._gad === 'string' && cartAttributes._gad !== '') {
      return true;
    }

    const globalData = window.automatedDiscountsLiquidData;
    const globalCartAttributes = globalData && typeof globalData === 'object' ? globalData.cartAttributes : null;

    return Boolean(globalCartAttributes && typeof globalCartAttributes._gad === 'string' && globalCartAttributes._gad !== '');
  }

  function decodePv2Offer(variantId) {
    const token = getPv2Token();
    const tokenParts = token.split('.');

    if (tokenParts.length < 2 || typeof window.atob !== 'function') {
      return {
        price: NaN,
      };
    }

    try {
      const offerRecord = JSON.parse(decodeBase64Url(tokenParts[1]));
      const offerId = offerRecord && offerRecord.o ? String(offerRecord.o) : '';
      const offerVariantId = offerId.split('_').pop();

      if (variantId && offerVariantId && offerVariantId !== String(variantId)) {
        return {
          price: NaN,
        };
      }

      if (Number.isFinite(Number(offerRecord.exp)) && Date.now() / 1000 > Number(offerRecord.exp)) {
        return {
          price: NaN,
        };
      }

      return {
        price: Number(offerRecord ? offerRecord.p : NaN),
      };
    } catch (error) {
      return {
        price: NaN,
      };
    }
  }

  function getGoogleOffer(discountData, variantId) {
    const gadOffer = decodeGoogleOffer(discountData.cartAttributes, variantId);

    if (Number.isFinite(gadOffer.price)) {
      return gadOffer;
    }

    return decodePv2Offer(variantId);
  }

  function getProductJsonPath() {
    return `${window.location.pathname.replace(/\/$/, '')}.js`;
  }

  async function getProductJson() {
    const nextProductJsonPath = getProductJsonPath();

    if (productJsonPromise && productJsonPath === nextProductJsonPath) {
      return productJsonPromise;
    }

    productJsonPath = nextProductJsonPath;
    productJsonPromise = window.fetch(nextProductJsonPath, {
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
      },
    }).then((response) => {
      if (!response.ok) {
        throw new Error(`Unable to load product JSON: ${response.status}`);
      }

      return response.json();
    }).catch(() => null);

    return productJsonPromise;
  }

  function getSelectedVariantId(discountData) {
    if (discountData.variantId) {
      return discountData.variantId;
    }

    const selectedVariantInput = document.querySelector('form[action*="/cart/add"] [name="id"]');

    return selectedVariantInput ? selectedVariantInput.value : '';
  }

  function getVariantPricing(productJson, variantId) {
    if (!productJson || !Array.isArray(productJson.variants) || variantId === '') {
      return {
        price: NaN,
        compareAtPrice: NaN,
      };
    }

    const variant = productJson.variants.find((candidate) => String(candidate.id) === String(variantId));

    if (!variant) {
      return {
        price: NaN,
        compareAtPrice: NaN,
      };
    }

    return {
      price: Number(variant.price) / 100,
      compareAtPrice: Number(variant.compare_at_price) / 100,
    };
  }

  function getDecimalPlaces(value) {
    const decimalPart = String(value).split('.')[1];

    return decimalPart ? decimalPart.length : 0;
  }

  function formatMoneyFromSample(amount, sampleText) {
    const fallback = `$${amount.toFixed(2)}`;

    if (typeof sampleText !== 'string' || sampleText.trim() === '') {
      return fallback;
    }

    const match = sampleText.match(/-?\d[\d,]*(?:\.\d+)?/);

    if (!match) {
      return fallback;
    }

    const sampleNumber = match[0];
    const decimalPlaces = getDecimalPlaces(sampleNumber);
    const hasThousandsSeparator = sampleNumber.includes(',');
    let formattedAmount = amount.toFixed(decimalPlaces);

    if (hasThousandsSeparator) {
      const amountParts = formattedAmount.split('.');
      amountParts[0] = amountParts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      formattedAmount = amountParts.join('.');
    }

    return `${sampleText.slice(0, match.index)}${formattedAmount}${sampleText.slice(match.index + sampleNumber.length)}`;
  }

  function getCompareAtPriceTextFromSavings(displayPriceText, savingsText, moneyFormatSample) {
    const displayPrice = parseMoney(displayPriceText);
    const savingsAmount = parseMoney(savingsText);

    if (!Number.isFinite(displayPrice) || !Number.isFinite(savingsAmount)) {
      return '';
    }

    return formatMoneyFromSample(displayPrice + savingsAmount, moneyFormatSample);
  }

  function createCompareAtPrice(compareAtPriceText) {
    const compareAtPrice = document.createElement('span');

    compareAtPrice.className = 'product-info__custom-price-google-compare text-subdued';
    compareAtPrice.setAttribute(CODEX_COMPARE_ATTRIBUTE, 'true');
    setCompareAtPriceText(compareAtPrice, compareAtPriceText);

    return compareAtPrice;
  }

  function getDisplayedCompareAtPrice(priceRow) {
    return priceRow.querySelector(`${CODEX_COMPARE_SELECTOR}:not([${CODEX_LIQUID_COMPARE_ATTRIBUTE}])`);
  }

  function setCompareAtPriceText(compareAtPrice, compareAtPriceText) {
    let screenReaderLabel = compareAtPrice.querySelector('.sr-only');

    compareAtPrice.setAttribute(CODEX_COMPARE_TEXT_ATTRIBUTE, compareAtPriceText);
    compareAtPrice.setAttribute('aria-label', `Regular price ${compareAtPriceText}`);

    if (
      screenReaderLabel instanceof HTMLElement &&
      screenReaderLabel.parentElement === compareAtPrice &&
      screenReaderLabel.textContent === 'Regular price' &&
      compareAtPrice.childNodes.length === 2 &&
      compareAtPrice.childNodes[1].nodeType === Node.TEXT_NODE &&
      compareAtPrice.childNodes[1].textContent === compareAtPriceText
    ) {
      return;
    }

    screenReaderLabel = document.createElement('span');
    screenReaderLabel.className = 'sr-only';
    screenReaderLabel.textContent = 'Regular price';

    compareAtPrice.replaceChildren(screenReaderLabel, document.createTextNode(compareAtPriceText));
  }

  function syncSalePrice(salePrice, salePriceText) {
    const currentSalePrice = parseMoney(salePrice.textContent || '');
    const nextSalePrice = parseMoney(salePriceText);

    if (Number.isFinite(currentSalePrice) && Number.isFinite(nextSalePrice) && Math.abs(currentSalePrice - nextSalePrice) < 0.005) {
      return;
    }

    const screenReaderLabel = document.createElement('span');

    screenReaderLabel.className = 'sr-only';
    screenReaderLabel.textContent = 'Sale price';

    salePrice.replaceChildren(screenReaderLabel, document.createTextNode(salePriceText));
  }

  function createDiscountBadge(savingsText) {
    const badge = document.createElement('on-sale-badge');

    badge.className = 'badge badge--on-sale';
    badge.setAttribute(CODEX_BADGE_ATTRIBUTE, 'true');
    badge.textContent = `Save ${savingsText}`;

    return badge;
  }

  function getLiquidCompareAtPrice(priceRow) {
    return priceRow.querySelector(`[${CODEX_LIQUID_COMPARE_ATTRIBUTE}]`);
  }

  function getLiquidOriginalPrice(priceRow) {
    const liquidCompareAtPrice = getLiquidCompareAtPrice(priceRow);

    if (!liquidCompareAtPrice) {
      return NaN;
    }

    const cents = Number.parseFloat(liquidCompareAtPrice.getAttribute(CODEX_ORIGINAL_PRICE_CENTS_ATTRIBUTE) || '');

    if (Number.isFinite(cents)) {
      return cents / 100;
    }

    return parseMoney(liquidCompareAtPrice.getAttribute(CODEX_ORIGINAL_PRICE_TEXT_ATTRIBUTE) || '');
  }

  function getLiquidOriginalPriceText(priceRow) {
    const liquidCompareAtPrice = getLiquidCompareAtPrice(priceRow);

    return liquidCompareAtPrice ? liquidCompareAtPrice.getAttribute(CODEX_ORIGINAL_PRICE_TEXT_ATTRIBUTE) || '' : '';
  }

  function isLiquidCompareAtPrice(compareAtPrice) {
    return compareAtPrice.hasAttribute(CODEX_LIQUID_COMPARE_ATTRIBUTE);
  }

  function normalizeLiquidCompareAtPriceSource(liquidCompareAtPrice) {
    if (!liquidCompareAtPrice) {
      return;
    }

    liquidCompareAtPrice.removeAttribute(CODEX_COMPARE_ATTRIBUTE);
    liquidCompareAtPrice.className = 'product-info__custom-price-google-source';
    liquidCompareAtPrice.hidden = true;
    liquidCompareAtPrice.setAttribute('aria-hidden', 'true');
    liquidCompareAtPrice.style.removeProperty('display');
    liquidCompareAtPrice.style.removeProperty('text-decoration-line');
  }

  function setGoogleDiscountActive(priceRow, isActive) {
    const priceMain = priceRow.closest('.product-info__custom-price-main');

    if (isActive) {
      if (!priceRow.hasAttribute(CODEX_ACTIVE_ATTRIBUTE)) {
        priceRow.setAttribute(CODEX_ACTIVE_ATTRIBUTE, 'true');
      }

      if (priceMain && !priceMain.classList.contains(PRICE_MAIN_COMPARE_CLASS)) {
        priceMain.classList.add(PRICE_MAIN_COMPARE_CLASS);
        priceMain.setAttribute(CODEX_PRICE_MAIN_COMPARE_ATTRIBUTE, 'true');
      }

      return;
    }

    priceRow.removeAttribute(CODEX_ACTIVE_ATTRIBUTE);

    if (priceMain && priceMain.hasAttribute(CODEX_PRICE_MAIN_COMPARE_ATTRIBUTE)) {
      priceMain.classList.remove(PRICE_MAIN_COMPARE_CLASS);
      priceMain.removeAttribute(CODEX_PRICE_MAIN_COMPARE_ATTRIBUTE);
    }
  }

  function removeInjectedCompareAtPrice(priceRow, preserveLiquidCompareAtPrice = true) {
    normalizeLiquidCompareAtPriceSource(getLiquidCompareAtPrice(priceRow));
    priceRow.removeAttribute(CODEX_COMPARE_TEXT_ATTRIBUTE);

    priceRow.querySelectorAll(CODEX_COMPARE_SELECTOR).forEach((injectedCompareAtPrice) => {
      if (!preserveLiquidCompareAtPrice || !isLiquidCompareAtPrice(injectedCompareAtPrice)) {
        injectedCompareAtPrice.remove();
        return;
      }

      injectedCompareAtPrice.hidden = false;
      injectedCompareAtPrice.style.removeProperty('display');
      injectedCompareAtPrice.style.removeProperty('text-decoration-line');
    });

    restoreHiddenPriceListItems(priceRow);
    restoreHiddenCompareAtPrices(priceRow);
    setGoogleDiscountActive(priceRow, false);
  }

  function removeNonNativeCompareAtPrices(priceRow) {
    removeInjectedCompareAtPrice(priceRow);

    priceRow.querySelectorAll('compare-at-price').forEach((compareAtPrice) => {
      compareAtPrice.remove();
    });
  }

  function hideGoogleCompareAtPrices(priceRow) {
    priceRow.querySelectorAll('compare-at-price').forEach((compareAtPrice) => {
      if (!compareAtPrice.hasAttribute(CODEX_HIDDEN_COMPARE_ATTRIBUTE)) {
        compareAtPrice.setAttribute(CODEX_HIDDEN_COMPARE_ATTRIBUTE, 'true');
      }

      if (!compareAtPrice.hidden) {
        compareAtPrice.hidden = true;
      }

      if (compareAtPrice.style.display !== 'none') {
        compareAtPrice.style.display = 'none';
      }
    });
  }

  function hideExtraPriceListItems(priceList, salePrice) {
    Array.from(priceList.children).forEach((priceItem) => {
      if (priceItem === salePrice) {
        return;
      }

      priceItem.setAttribute(CODEX_HIDDEN_PRICE_LIST_ITEM_ATTRIBUTE, 'true');
      priceItem.hidden = true;
      priceItem.style.display = 'none';
    });
  }

  function restoreHiddenPriceListItems(priceRow) {
    priceRow.querySelectorAll(`[${CODEX_HIDDEN_PRICE_LIST_ITEM_ATTRIBUTE}]`).forEach((priceItem) => {
      priceItem.removeAttribute(CODEX_HIDDEN_PRICE_LIST_ITEM_ATTRIBUTE);
      priceItem.hidden = false;
      priceItem.style.removeProperty('display');
    });
  }

  function restoreHiddenCompareAtPrices(priceRow) {
    priceRow.querySelectorAll(`compare-at-price[${CODEX_HIDDEN_COMPARE_ATTRIBUTE}]`).forEach((compareAtPrice) => {
      compareAtPrice.removeAttribute(CODEX_HIDDEN_COMPARE_ATTRIBUTE);
      compareAtPrice.hidden = false;
      compareAtPrice.style.removeProperty('display');
    });
  }

  function hideConflictingCompareAtPrices(priceRow, priceList, salePrice, compareAtPrice, displayPriceText) {
    const displayPrice = parseMoney(displayPriceText);

    priceRow.querySelectorAll('compare-at-price, s, del, [style*="line-through"], .text-subdued').forEach((candidate) => {
      if (
        candidate === compareAtPrice ||
        compareAtPrice.contains(candidate) ||
        candidate === salePrice ||
        salePrice.contains(candidate) ||
        candidate.closest(CODEX_BADGE_SELECTOR)
      ) {
        return;
      }

      if (candidate.hasAttribute(CODEX_LIQUID_COMPARE_ATTRIBUTE)) {
        normalizeLiquidCompareAtPriceSource(candidate);
        return;
      }

      if (priceList.contains(candidate)) {
        candidate.setAttribute(CODEX_HIDDEN_PRICE_LIST_ITEM_ATTRIBUTE, 'true');
        candidate.hidden = true;
        candidate.style.display = 'none';
        return;
      }

      const candidatePrice = parseMoney(candidate.textContent || '');

      if (!Number.isFinite(displayPrice) || !Number.isFinite(candidatePrice) || Math.abs(candidatePrice - displayPrice) < 0.005) {
        candidate.setAttribute(CODEX_HIDDEN_PRICE_LIST_ITEM_ATTRIBUTE, 'true');
        candidate.hidden = true;
        candidate.style.display = 'none';
      }
    });
  }

  function forceActiveCompareAtPrices() {
    document.querySelectorAll(`.product-info__custom-price-price-row[${CODEX_ACTIVE_ATTRIBUTE}][${CODEX_COMPARE_TEXT_ATTRIBUTE}]`).forEach((priceRow) => {
      const compareAtPriceText = priceRow.getAttribute(CODEX_COMPARE_TEXT_ATTRIBUTE) || '';
      const priceList = priceRow.querySelector('.price-list');
      const salePrice = priceList ? priceList.querySelector('sale-price') : null;
      const compareAtPrice = getDisplayedCompareAtPrice(priceRow);

      if (!priceList || !salePrice || !compareAtPrice || compareAtPriceText === '') {
        return;
      }

      compareAtPrice.hidden = false;
      compareAtPrice.style.display = 'inline';
      compareAtPrice.style.removeProperty('text-decoration-line');
      setCompareAtPriceText(compareAtPrice, compareAtPriceText);

      if (compareAtPrice.parentElement !== priceRow || compareAtPrice.previousElementSibling !== priceList) {
        priceList.insertAdjacentElement('afterend', compareAtPrice);
      }

      hideGoogleCompareAtPrices(priceRow);
      hideExtraPriceListItems(priceList, salePrice);
      hideConflictingCompareAtPrices(priceRow, priceList, salePrice, compareAtPrice, salePrice.textContent || '');
    });
  }

  function scheduleCompareAtEnforcement() {
    if (compareEnforcementScheduled) {
      return;
    }

    compareEnforcementScheduled = true;

    COMPARE_ENFORCEMENT_DELAYS.forEach((delay, index) => {
      window.setTimeout(() => {
        forceActiveCompareAtPrices();

        if (index === COMPARE_ENFORCEMENT_DELAYS.length - 1) {
          compareEnforcementScheduled = false;
        }
      }, delay);
    });
  }

  function syncCompareAtPrice(priceRow, priceList, salePrice, compareAtPriceText, displayPriceText) {
    setGoogleDiscountActive(priceRow, true);
    priceRow.setAttribute(CODEX_COMPARE_TEXT_ATTRIBUTE, compareAtPriceText);
    normalizeLiquidCompareAtPriceSource(getLiquidCompareAtPrice(priceRow));
    hideGoogleCompareAtPrices(priceRow);
    hideExtraPriceListItems(priceList, salePrice);

    let compareAtPrice = getDisplayedCompareAtPrice(priceRow);

    if (!compareAtPrice) {
      compareAtPrice = createCompareAtPrice(compareAtPriceText);
      priceList.insertAdjacentElement('afterend', compareAtPrice);
    }

    priceRow.querySelectorAll(CODEX_COMPARE_SELECTOR).forEach((candidate) => {
      if (candidate !== compareAtPrice && !isLiquidCompareAtPrice(candidate)) {
        candidate.remove();
      }
    });

    if (!compareAtPrice.hasAttribute(CODEX_COMPARE_ATTRIBUTE)) {
      compareAtPrice.setAttribute(CODEX_COMPARE_ATTRIBUTE, 'true');
    }

    compareAtPrice.setAttribute(CODEX_COMPARE_TEXT_ATTRIBUTE, compareAtPriceText);

    if (compareAtPrice.className !== 'product-info__custom-price-google-compare text-subdued') {
      compareAtPrice.className = 'product-info__custom-price-google-compare text-subdued';
    }
    compareAtPrice.hidden = false;
    compareAtPrice.style.display = 'inline';
    compareAtPrice.style.removeProperty('text-decoration-line');
    setCompareAtPriceText(compareAtPrice, compareAtPriceText);

    if (compareAtPrice.parentElement !== priceRow || compareAtPrice.previousElementSibling !== priceList) {
      priceList.insertAdjacentElement('afterend', compareAtPrice);
    }

    priceRow.querySelectorAll('compare-at-price').forEach((candidate) => {
      setCompareAtPriceText(candidate, compareAtPriceText);
    });

    hideConflictingCompareAtPrices(priceRow, priceList, salePrice, compareAtPrice, displayPriceText);
    scheduleCompareAtEnforcement();
  }

  function removeInjectedDiscountBadge(priceRow) {
    const injectedBadge = priceRow.querySelector(CODEX_BADGE_SELECTOR);

    if (!injectedBadge) {
      return;
    }

    const badgeList = injectedBadge.closest('.product-info__badge-list');

    injectedBadge.remove();

    if (badgeList && badgeList.hasAttribute(CODEX_BADGE_LIST_ATTRIBUTE) && badgeList.children.length === 0) {
      badgeList.remove();
    }
  }

  function syncDiscountBadge(priceRow, priceList, savingsText) {
    let badgeList = priceRow.querySelector('.product-info__badge-list');
    let badge = priceRow.querySelector(CODEX_BADGE_SELECTOR);

    if (!badgeList) {
      badgeList = document.createElement('div');
      badgeList.className = 'product-info__badge-list';
      badgeList.setAttribute(CODEX_BADGE_LIST_ATTRIBUTE, 'true');
      (priceRow.querySelector(CODEX_COMPARE_SELECTOR) || priceList).insertAdjacentElement('afterend', badgeList);
    }

    if (!badge) {
      badge = createDiscountBadge(savingsText);
      badgeList.append(badge);
      return;
    }

    const nextBadgeText = `Save ${savingsText}`;

    if (badge.textContent.trim() !== nextBadgeText) {
      badge.textContent = nextBadgeText;
    }
  }

  async function syncGoogleAutomatedDiscountCompareAt() {
    const productInfo = document.querySelector(PRODUCT_INFO_SELECTOR);
    const priceBlock = productInfo ? productInfo.querySelector(CUSTOM_PRICE_BLOCK_SELECTOR) : null;
    const appBlock = productInfo ? productInfo.querySelector(GOOGLE_DISCOUNT_BLOCK_SELECTOR) : null;

    if (!priceBlock || !appBlock) {
      return false;
    }

    const priceList = priceBlock.querySelector('.product-info__custom-price-price-row .price-list');
    const priceRow = priceList ? priceList.closest('.product-info__custom-price-price-row') : null;
    const salePrice = priceList ? priceList.querySelector('sale-price') : null;

    if (!priceList || !priceRow || !salePrice) {
      return false;
    }

    const discountData = getGoogleDiscountData(appBlock);
    const variantId = getSelectedVariantId(discountData);
    const googleOffer = getGoogleOffer(discountData, variantId);
    const liquidCompareAtPrice = getLiquidCompareAtPrice(priceRow);
    const liquidOriginalPrice = getLiquidOriginalPrice(priceRow);
    const liquidOriginalPriceText = getLiquidOriginalPriceText(priceRow);
    const visiblePrice = parseMoney(salePrice.textContent || '');
    let variantPricing = {
      price: NaN,
      compareAtPrice: NaN,
    };
    let originalPrice = liquidOriginalPrice;
    let hasNativeShopifyCompareAt = !liquidCompareAtPrice && Boolean(priceList.querySelector('compare-at-price'));

    if (!Number.isFinite(originalPrice) && !hasNativeShopifyCompareAt) {
      const productJson = await getProductJson();

      variantPricing = getVariantPricing(productJson, variantId);
      originalPrice = variantPricing.price;
      hasNativeShopifyCompareAt = Number.isFinite(variantPricing.compareAtPrice) && variantPricing.compareAtPrice > originalPrice;
    }

    const moneyFormatSample = liquidOriginalPriceText || discountData.formattedCurrentPrice || '$0.00';
    const originalPriceText = Number.isFinite(originalPrice)
      ? (Number.isFinite(liquidOriginalPrice) && Math.abs(originalPrice - liquidOriginalPrice) < 0.005 && liquidOriginalPriceText !== '' ? liquidOriginalPriceText : formatMoneyFromSample(originalPrice, moneyFormatSample))
      : liquidOriginalPriceText;
    const googleOfferPrice = googleOffer.price;
    const displayPrice = Number.isFinite(googleOfferPrice) && Number.isFinite(originalPrice) && googleOfferPrice < originalPrice - 0.005 ? googleOfferPrice : visiblePrice;
    const displayPriceText = Number.isFinite(displayPrice) ? formatMoneyFromSample(displayPrice, moneyFormatSample) : '';
    const savingsAmount = Number.isFinite(originalPrice) && Number.isFinite(displayPrice) ? originalPrice - displayPrice : NaN;
    const savingsText = Number.isFinite(savingsAmount) ? formatMoneyFromSample(savingsAmount, moneyFormatSample) : '';
    const compareAtPriceText = getCompareAtPriceTextFromSavings(displayPriceText, savingsText, moneyFormatSample) || originalPriceText;
    const hasGoogleDiscount = Number.isFinite(originalPrice) && Number.isFinite(displayPrice) && displayPrice < originalPrice - 0.005;

    if (hasNativeShopifyCompareAt) {
      removeInjectedCompareAtPrice(priceRow, false);
      removeInjectedDiscountBadge(priceRow);
      return false;
    }

    if (!hasGoogleDiscount || originalPriceText === '' || savingsText === '') {
      removeNonNativeCompareAtPrices(priceRow);
      removeInjectedDiscountBadge(priceRow);
      return false;
    }

    if (displayPriceText !== '') {
      syncSalePrice(salePrice, displayPriceText);
    }

    syncCompareAtPrice(priceRow, priceList, salePrice, compareAtPriceText, displayPriceText);
    syncDiscountBadge(priceRow, priceList, savingsText);

    return true;
  }

  function queueSync() {
    if (syncQueued) {
      return;
    }

    syncQueued = true;

    window.requestAnimationFrame(async () => {
      syncQueued = false;

      if (syncInProgress) {
        syncAfterCurrentRun = true;
        return;
      }

      syncInProgress = true;

      try {
        const hasActiveGoogleDiscount = await syncGoogleAutomatedDiscountCompareAt();

        if (hasActiveGoogleDiscount) {
          observeProductInfo();
        }
      } finally {
        syncInProgress = false;

        if (syncAfterCurrentRun) {
          syncAfterCurrentRun = false;
          queueSync();
        }
      }
    });
  }

  function observeProductInfo() {
    const productInfo = document.querySelector(PRODUCT_INFO_SELECTOR);

    if (!(productInfo instanceof HTMLElement) || observedProductInfo === productInfo) {
      return;
    }

    if (observer) {
      observer.disconnect();
    }

    observedProductInfo = productInfo;
    observer = new MutationObserver(queueSync);
    observer.observe(productInfo, {
      childList: true,
      characterData: true,
      subtree: true,
    });
  }

  function scheduleInitialSyncs() {
    if (!hasGoogleOfferSignal()) {
      return;
    }

    SYNC_DELAYS.forEach((delay) => {
      window.setTimeout(queueSync, delay);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scheduleInitialSyncs, { once: true });
  } else {
    scheduleInitialSyncs();
  }

  document.addEventListener('shopify:section:load', scheduleInitialSyncs);
})();

/*
  CODEX CUSTOMIZATION START
  Scope: Rise.ai gift card product template
  Notes:
  - Rise.ai injects its Send as a Gift button into the theme's buy-buttons element.
  - Product variant rerenders replace that element, so preserve and reattach the injected node on the gift-card template.
  CODEX CUSTOMIZATION END
*/

(() => {
  const GIFT_CARD_TEMPLATE = 'gift-card';
  const BUY_BUTTONS_SELECTOR = `buy-buttons[template="${GIFT_CARD_TEMPLATE}"]`;
  const RISE_BUTTON_SELECTOR = '.GiftWizard-gift-button, .gwbutton';
  const RISE_UPSELL_SELECTOR = '.gbbExtBundleUpsellWrapper';
  const READY_CLASS = 'codex-rise-gift-ready';
  const SYNC_DELAYS = [0, 100, 300, 750, 1500, 3000, 5000];

  const preservedNodesByForm = new Map();
  let syncQueued = false;
  let observer = null;

  function getState(formId) {
    if (!preservedNodesByForm.has(formId)) {
      preservedNodesByForm.set(formId, {});
    }

    return preservedNodesByForm.get(formId);
  }

  function rememberInjectedNodes(buyButtons, state) {
    const riseButtons = Array.from(buyButtons.querySelectorAll(RISE_BUTTON_SELECTOR));
    const riseUpsellWrapper = buyButtons.querySelector(RISE_UPSELL_SELECTOR);

    if (riseButtons.length > 0) {
      const preferredButton = state.riseButton instanceof HTMLElement && riseButtons.includes(state.riseButton) ? state.riseButton : riseButtons[0];

      state.riseButton = preferredButton;

      riseButtons.forEach((riseButton) => {
        if (riseButton !== preferredButton) {
          riseButton.remove();
        }
      });
    }

    if (riseUpsellWrapper instanceof HTMLElement) {
      state.riseUpsellWrapper = riseUpsellWrapper;
    }
  }

  function restoreInjectedNodes(buyButtons, state) {
    if (state.riseButton instanceof HTMLElement && !buyButtons.contains(state.riseButton)) {
      buyButtons.append(state.riseButton);
    }

    if (state.riseUpsellWrapper instanceof HTMLElement && !buyButtons.contains(state.riseUpsellWrapper)) {
      buyButtons.append(state.riseUpsellWrapper);
    }
  }

  function syncRiseGiftButtons() {
    document.querySelectorAll(BUY_BUTTONS_SELECTOR).forEach((buyButtons) => {
      const formId = buyButtons.getAttribute('form') || '';

      if (formId === '') {
        return;
      }

      const state = getState(formId);

      rememberInjectedNodes(buyButtons, state);
      restoreInjectedNodes(buyButtons, state);
      rememberInjectedNodes(buyButtons, state);

      buyButtons.classList.toggle(READY_CLASS, state.riseButton instanceof HTMLElement && buyButtons.contains(state.riseButton));
    });
  }

  function queueSync() {
    if (syncQueued) {
      return;
    }

    syncQueued = true;

    window.requestAnimationFrame(() => {
      syncQueued = false;
      syncRiseGiftButtons();
    });
  }

  function scheduleSyncs() {
    SYNC_DELAYS.forEach((delay) => {
      window.setTimeout(queueSync, delay);
    });
  }

  function hasGiftCardBuyButtons() {
    return document.querySelector(BUY_BUTTONS_SELECTOR) !== null;
  }

  function formHasGiftCardBuyButtons(form) {
    if (!(form instanceof HTMLFormElement) || form.id === '') {
      return false;
    }

    return Array.from(document.querySelectorAll(BUY_BUTTONS_SELECTOR)).some((buyButtons) => buyButtons.getAttribute('form') === form.id);
  }

  function startRiseGiftCardSync() {
    if (!hasGiftCardBuyButtons()) {
      return;
    }

    syncRiseGiftButtons();
    scheduleSyncs();

    if (!observer) {
      observer = new MutationObserver(queueSync);
      observer.observe(document.documentElement, {
        childList: true,
        subtree: true,
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', startRiseGiftCardSync, { once: true });
  } else {
    startRiseGiftCardSync();
  }

  document.addEventListener('product:rerender', (event) => {
    if (formHasGiftCardBuyButtons(event.target)) {
      scheduleSyncs();
    }
  }, true);

  document.addEventListener('variant:change', (event) => {
    if (formHasGiftCardBuyButtons(event.target)) {
      scheduleSyncs();
    }
  });

  document.addEventListener('shopify:section:load', startRiseGiftCardSync);
})();
